import { useEffect, useState } from 'react';
import { Reaction } from '../../hooks/useReactions';

interface ReactionAnimationProps {
  reactions: Reaction[];
}

interface AnimatedReaction extends Reaction {
  x: number;
  y: number;
  opacity: number;
}

export function ReactionAnimation({ reactions }: ReactionAnimationProps) {
  const [animatedReactions, setAnimatedReactions] = useState<AnimatedReaction[]>([]);

  useEffect(() => {
    // Add new reactions with random starting positions
    reactions.forEach((reaction) => {
      const exists = animatedReactions.some((ar) => ar.id === reaction.id);
      if (!exists) {
        const newAnimatedReaction: AnimatedReaction = {
          ...reaction,
          x: Math.random() * 80 + 10, // 10-90% from left
          y: 100, // Start at bottom
          opacity: 1,
        };
        setAnimatedReactions((prev) => [...prev, newAnimatedReaction]);
      }
    });

    // Remove reactions that are no longer in the list
    setAnimatedReactions((prev) =>
      prev.filter((ar) => reactions.some((r) => r.id === ar.id))
    );
  }, [reactions]);

  // Animate reactions floating up
  useEffect(() => {
    if (animatedReactions.length === 0) return;

    const interval = setInterval(() => {
      setAnimatedReactions((prev) =>
        prev.map((ar) => ({
          ...ar,
          y: ar.y - 2, // Move up
          opacity: Math.max(0, ar.opacity - 0.02), // Fade out
        }))
      );
    }, 50);

    return () => clearInterval(interval);
  }, [animatedReactions.length]);

  if (animatedReactions.length === 0) return null;

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-50">
      {animatedReactions.map((reaction) => (
        <div
          key={reaction.id}
          className="absolute text-4xl transition-all duration-100"
          style={{
            left: `${reaction.x}%`,
            bottom: `${reaction.y}%`,
            opacity: reaction.opacity,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex flex-col items-center">
            <span className="text-4xl drop-shadow-lg">{reaction.emoji}</span>
            <span className="text-xs text-white bg-black/50 px-2 py-0.5 rounded-full mt-1 whitespace-nowrap">
              {reaction.participantName}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}
